import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { useBrainStore } from '@/state/brainStore';
import type { StudioId } from '@/domain/types';
import InfoTip from '@/components/InfoTip';
import { ReportBarChart } from '@/components/reporting/ReportChart';
import { REPORT_COLORS } from '@/components/reporting/reportChartTheme';
import { aud } from '@/utils/format';
import {
  weeksBookedAhead,
  leverFunnels,
  datelessDepositCohort,
  businessLeaks,
  WEEKS_BOOKED_THRESHOLD,
  type LeakCategoryKey,
} from '@/domain/reportingEngine';

interface Props {
  /** The selected date-range window (funnels + reschedule leak drill-down). */
  days: readonly string[];
  /** Studio scope, or undefined for all studios. */
  studioId?: StudioId;
}

function pctLabel(n: number | null): string {
  return n === null ? '—' : `${Math.round(n)}%`;
}

function leakDateLabel(iso: string): string {
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}

/**
 * Levers tab (Prompt G, Stage 4) - the leak reports. Answers Zack's questions:
 * how many weeks ahead each store is booked, how many of the week's deposits /
 * reschedules / rebooks / confirmations landed, how many deposits sit dateless,
 * and - for the leak - who rescheduled and what their pathway was. Every number
 * comes from `reportingEngine` selectors; the tab holds no inline metrics.
 */
export default function LeversTab({ days, studioId }: Props) {
  const bookings = useBrainStore((s) => s.bookings);
  const deposits = useBrainStore((s) => s.deposits);
  const artists = useBrainStore((s) => s.artists);
  const confirmationCalls = useBrainStore((s) => s.confirmationCalls);
  const studios = useBrainStore((s) => s.studios);
  const navigate = useNavigate();
  const [activeLeak, setActiveLeak] = useState<LeakCategoryKey>('reschedule-leaked');
  const [openRecordId, setOpenRecordId] = useState<string | null>(null);

  const activeStudios = useMemo(() => Object.values(studios).filter((s) => !s.archived), [studios]);
  const weeksStudios = useMemo(
    () => (studioId ? activeStudios.filter((s) => s.id === studioId) : activeStudios),
    [activeStudios, studioId],
  );

  const weeksByStudio = useMemo(
    () => weeksStudios.map((s) => ({ studio: s, ahead: weeksBookedAhead(s.id) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [weeksStudios, bookings, artists],
  );

  const funnels = useMemo(
    () => leverFunnels(days, studioId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [days, studioId, bookings, deposits, confirmationCalls],
  );

  const dateless = useMemo(
    () => datelessDepositCohort(studioId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [studioId, deposits, bookings],
  );

  const leaks = useMemo(
    () => businessLeaks(days, studioId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [days, studioId, bookings, deposits, confirmationCalls],
  );

  const funnelChart = funnels.map((f) => ({
    lever: f.label,
    Landed: f.converted,
    Missed: f.missed,
  }));

  const activeCategory = leaks.categories.find((c) => c.key === activeLeak) ?? leaks.categories[0];

  const thresholdLabel = `${Math.round(WEEKS_BOOKED_THRESHOLD * 100)}%`;

  return (
    <div className="space-y-6 max-w-6xl" data-testid="reporting-levers">
      {/* Weeks booked ahead, per store */}
      <section className="panel p-4" data-testid="levers-weeks-ahead">
        <h2 className="heading-serif text-lg text-cream mb-1">
          Weeks booked ahead
          <InfoTip>
            How many whole weeks from today each store is at least {thresholdLabel} utilised (real booked hours ÷
            rostered capacity) before the first week with free capacity. The forward-book depth Zack asks for.
          </InfoTip>
        </h2>
        <p className="font-inter text-[10px] text-cream-faint uppercase tracking-widest mb-3">
          Consecutive weeks ≥ {thresholdLabel} utilised before the first with slack
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {weeksByStudio.map(({ studio, ahead }) => (
            <div
              key={studio.id}
              className="rounded-lg border border-gold-dim/20 bg-ink-soft/30 px-3 py-3"
              data-testid={`levers-week-tile-${studio.id}`}
            >
              <p className="font-inter text-[10px] uppercase tracking-widest text-cream-soft truncate">{studio.name}</p>
              <p className="font-mono text-2xl text-gold-bright mt-1">{ahead.weeks}</p>
              <p className="font-inter text-[10px] text-cream-faint mt-0.5">
                {ahead.weeks === 1 ? 'week' : 'weeks'} · this week {Math.round((ahead.weekly[0]?.utilisedPct ?? 0) * 100)}%
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* The four lever conversions */}
      <section className="panel p-4" data-testid="levers-funnels">
        <h2 className="heading-serif text-lg text-cream mb-1">
          Lever conversions
          <InfoTip>
            Of the deposits, reschedules, in-chair rebooks and confirmations in this window, how many landed the next
            step: a dated booking, a new date, a booked rebook, and an actual show. The gap is the leak.
          </InfoTip>
        </h2>
        <p className="font-inter text-[10px] text-cream-faint uppercase tracking-widest mb-3">
          Landed vs missed for each of the three levers plus confirmations
        </p>
        <ReportBarChart
          data={funnelChart}
          xKey="lever"
          stacked
          series={[
            { key: 'Landed', name: 'Landed', color: REPORT_COLORS.mint },
            { key: 'Missed', name: 'Missed', color: REPORT_COLORS.blood },
          ]}
        />
        <div className="overflow-x-auto mt-3">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-cream-soft">
                <th className="text-left px-2 py-2">Lever</th>
                <th className="text-right px-2 py-2">Landed</th>
                <th className="text-right px-2 py-2">Missed</th>
                <th className="text-right px-2 py-2">Rate</th>
                <th className="text-right px-2 py-2">At risk</th>
              </tr>
            </thead>
            <tbody>
              {funnels.map((f) => (
                <tr key={f.key} className="border-t border-gold-dim/15" data-testid={`levers-funnel-${f.key}`}>
                  <td className="px-2 py-2 font-inter text-cream whitespace-nowrap">{f.label}</td>
                  <td className="px-2 py-2 text-right font-mono text-mint">{f.converted}</td>
                  <td className="px-2 py-2 text-right font-mono text-blood">{f.missed}</td>
                  <td className="px-2 py-2 text-right font-mono text-gold-bright">{pctLabel(f.ratePct)}</td>
                  <td className="px-2 py-2 text-right font-mono text-cream-soft">{aud(f.valueAtRiskAud)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="font-inter text-[11px] text-cream-faint mt-2">
          At risk = the deposit money already taken that&rsquo;s tied to the missed count on that lever.
        </p>
      </section>

      {/* Dateless deposit cohort, deep-linking into the queue */}
      <section className="panel p-4" data-testid="levers-dateless">
        <h2 className="heading-serif text-lg text-cream mb-1">
          Deposits with no booking date
          <InfoTip>
            Paid deposits with no upcoming date booked - money on file with nowhere to land. Click through to work them
            in the operator queue.
          </InfoTip>
        </h2>
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="font-mono text-3xl text-gold-bright">{dateless.count}</p>
            <p className="font-inter text-[10px] text-cream-faint uppercase tracking-widest mt-1">
              deposits · {aud(dateless.amountAud)} held with no date
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/queue?kind=deposit-on-file')}
            data-testid="levers-dateless-link"
            className="rounded-md border border-gold-dim/40 bg-gold/10 px-3 py-1.5 text-xs font-inter uppercase tracking-widest text-gold-bright hover:bg-gold/20 transition"
          >
            Work these in the queue
          </button>
        </div>
      </section>

      {/* Where business leaks: every no-rebook path, as drill-down tabs */}
      <section className="panel p-4" data-testid="levers-leaks">
        <div className="flex items-baseline justify-between flex-wrap gap-2 mb-1">
          <h2 className="heading-serif text-lg text-cream flex items-center gap-1">
            Where business leaks
            <InfoTip>
              Every modelled path that ends in no rebook. Pick a category, then open a record to see its path. &ldquo;At
              risk&rdquo; is the deposit money already taken that&rsquo;s tied to that record - not a projection.
            </InfoTip>
          </h2>
          <p className="font-mono text-sm text-blood">
            {leaks.totalCount} records · {aud(leaks.totalValueAtRiskAud)} at risk
          </p>
        </div>

        {/* Category tabs */}
        <div className="flex flex-wrap gap-2 mt-3 mb-4" data-testid="levers-leak-tabs">
          {leaks.categories.map((c) => {
            const on = c.key === activeCategory?.key;
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => {
                  setActiveLeak(c.key);
                  setOpenRecordId(null);
                }}
                data-testid={`levers-leak-tab-${c.key}`}
                className={clsx(
                  'inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-inter transition',
                  on
                    ? 'border-blood/60 bg-blood/10 text-cream font-medium'
                    : 'border-gold-dim/30 text-cream-soft hover:border-gold-bright/50',
                )}
              >
                {c.label}
                <span
                  className={clsx(
                    'font-mono text-[11px] rounded-full px-1.5',
                    on ? 'bg-blood text-ink-black' : 'bg-ink-soft/50 text-cream',
                  )}
                >
                  {c.count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Records for the active category, each expandable to its path */}
        {activeCategory && activeCategory.records.length > 0 ? (
          <div className="space-y-1.5" data-testid="levers-leak-records">
            {activeCategory.records.map((r) => {
              const open = openRecordId === r.id;
              return (
                <div key={r.id} className="rounded-md border border-gold-dim/25 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setOpenRecordId(open ? null : r.id)}
                    data-testid={`levers-leak-record-${r.id}`}
                    className={clsx(
                      'w-full flex items-center justify-between gap-3 px-3 py-2 text-left transition',
                      open ? 'bg-blood/5' : 'hover:bg-ink-soft/40',
                    )}
                  >
                    <span className="min-w-0">
                      <span className="block font-inter text-sm text-cream truncate">{r.name}</span>
                      <span className="block font-inter text-[11px] text-cream-faint">{r.subtitle}</span>
                    </span>
                    <span className="flex items-center gap-3 shrink-0">
                      <span className="font-mono text-sm text-blood">{aud(r.valueAtRiskAud)}</span>
                      <span className="font-inter text-[11px] uppercase tracking-widest text-gold-bright">
                        {open ? 'Hide path' : 'See path'}
                      </span>
                    </span>
                  </button>
                  {open ? (
                    <ol
                      className="px-4 py-3 border-t border-gold-dim/20 space-y-2.5"
                      data-testid={`levers-leak-path-${r.id}`}
                    >
                      {r.path.map((step, i) => (
                        <li key={i} className="flex gap-3">
                          <span
                            className={clsx('mt-1 h-2 w-2 rounded-full shrink-0', step.leak ? 'bg-blood' : 'bg-gold-dim')}
                          />
                          <span className="min-w-0">
                            {step.atIso ? (
                              <span className="block font-mono text-[10px] text-cream-faint">{leakDateLabel(step.atIso)}</span>
                            ) : null}
                            <span className={clsx('block text-sm', step.leak ? 'text-blood font-medium' : 'text-cream')}>
                              {step.label}
                            </span>
                            {step.detail ? (
                              <span className="block font-serif text-xs text-cream-soft italic mt-0.5">
                                &ldquo;{step.detail}&rdquo;
                              </span>
                            ) : null}
                          </span>
                        </li>
                      ))}
                    </ol>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : (
          <p className="font-serif text-cream-soft text-sm">No records in this category for the window.</p>
        )}
      </section>
    </div>
  );
}
